// Progression locale, format version 2 : une entrée par morceau, et dans chaque entrée
// un résultat par combinaison de mains travaillées (main droite, main gauche, mains
// ensemble). Le statut du morceau n'est « réussi » que si toutes ses mains l'ont été
// ensemble, sur un instrument. Les données de la version 1 (sans mains ni source)
// sont conservées sous la clé « unknown » ; leur statut « réussi » est ramené à
// « commencé », car il pouvait avoir été obtenu sur une seule main.

import type { SessionSummary } from '../engine/session';
import type { InputKind } from '../engine/types';
import type { Hand } from '../music/model';
import { Store } from './store';

export type HandsKey = 'right' | 'left' | 'both' | 'unknown';
export type ProgressInput = InputKind | 'mixed' | 'unknown';
export type ScoreStatus = 'new' | 'started' | 'passed';

export interface BestResult {
  accuracyPitch: number;
  accuracyTiming: number;
  precision: number;
  coverage: number;
  quality: number;
  tempo: number;
  input: ProgressInput;
  at: string;
  /** Résultat migré depuis la version 1 : précision, couverture et score global sont inconnus. */
  legacy?: boolean;
}

export interface HandsProgress {
  attempts: number;
  lastAt: string;
  status: ScoreStatus;
  bestWait?: BestResult;
  bestTempo?: BestResult;
}

export interface ScoreProgress {
  attempts: number;
  lastAt: string;
  /** Statut du morceau entier : « réussi » seulement avec toutes ses mains, ensemble, sur un instrument. */
  status: ScoreStatus;
  hands: Partial<Record<HandsKey, HandsProgress>>;
}

export interface Progress {
  version: 2;
  scores: Record<string, ScoreProgress>;
  totalSessions: number;
  totalPracticeMs: number;
  lastScoreId: string | null;
}

export const DEFAULT_PROGRESS: Progress = { version: 2, scores: {}, totalSessions: 0, totalPracticeMs: 0, lastScoreId: null };

/** Seuils de réussite d'un exercice. */
export const PASS_RULE = {
  minCoverage: 0.8,
  minAccuracyPitch: 0.9,
  minAccuracyTiming: 0.8,
  minPrecision: 0.9,
  minTempoRatio: 0.8,
};

export function handsKey(hands: readonly Hand[]): HandsKey {
  if (hands.length >= 2) return 'both';
  if (hands.length === 1) return hands[0];
  return 'unknown';
}

/** Raisons pour lesquelles une session ne valide pas l'exercice (vide si elle le valide). */
export function passBlockers(summary: SessionSummary, targetTempo: number): string[] {
  const reasons: string[] = [];
  if (summary.mode !== 'tempo') reasons.push('la réussite se valide en mode tempo imposé');
  if (summary.expected === 0) reasons.push('aucune note attendue');
  if (!summary.instrument) reasons.push(summary.sources.length === 0 ? 'aucune note jouée' : 'la réussite se valide sur un instrument (micro ou MIDI), pas au clavier d’essai');
  if (summary.coverage < PASS_RULE.minCoverage) reasons.push(`captation insuffisante : ${Math.round(summary.coverage * 100)} % des notes évaluables (minimum ${Math.round(PASS_RULE.minCoverage * 100)} %)`);
  if (summary.accuracyPitch < PASS_RULE.minAccuracyPitch) reasons.push(`notes justes : ${Math.round(summary.accuracyPitch * 100)} % (minimum ${Math.round(PASS_RULE.minAccuracyPitch * 100)} %)`);
  if (summary.accuracyTiming < PASS_RULE.minAccuracyTiming) reasons.push(`en rythme : ${Math.round(summary.accuracyTiming * 100)} % (minimum ${Math.round(PASS_RULE.minAccuracyTiming * 100)} %)`);
  if (summary.precision < PASS_RULE.minPrecision) reasons.push(`trop de fausses notes ou de notes en trop : précision ${Math.round(summary.precision * 100)} % (minimum ${Math.round(PASS_RULE.minPrecision * 100)} %)`);
  if (summary.tempo < PASS_RULE.minTempoRatio * targetTempo) reasons.push(`tempo ${summary.tempo} inférieur à ${Math.round(PASS_RULE.minTempoRatio * 100)} % de la cible (${Math.ceil(PASS_RULE.minTempoRatio * targetTempo)})`);
  return reasons;
}

/**
 * Un exercice est réussi en mode tempo, sur un instrument, à au moins 80 % du tempo cible,
 * quand au moins 80 % des notes étaient évaluables et que, sur cette partie, 90 % des notes
 * sont justes, 80 % en rythme, et que les fausses notes et notes en trop restent rares
 * (précision ≥ 90 %).
 */
export function isPassed(summary: SessionSummary, targetTempo: number): boolean {
  return passBlockers(summary, targetTempo).length === 0;
}

function better(a: BestResult | undefined, b: BestResult): BestResult {
  if (!a) return b;
  if (b.quality > a.quality) return b;
  if (b.quality === a.quality && b.accuracyTiming > a.accuracyTiming) return b;
  if (b.quality === a.quality && b.accuracyTiming === a.accuracyTiming && b.tempo > a.tempo) return b;
  return a;
}

function inputOf(summary: SessionSummary): ProgressInput {
  if (summary.sources.length === 0) return 'unknown';
  if (summary.sources.length === 1) return summary.sources[0];
  return 'mixed';
}

/** Fonction pure : renvoie la progression mise à jour après une session. */
export function recordSession(progress: Progress, scoreId: string, summary: SessionSummary, targetTempo: number, durationMs: number, at: Date = new Date()): Progress {
  const key = handsKey(summary.hands);
  const previous: ScoreProgress = progress.scores[scoreId] ?? { attempts: 0, lastAt: '', status: 'new', hands: {} };
  const previousHands: HandsProgress = previous.hands[key] ?? { attempts: 0, lastAt: '', status: 'new' };
  const result: BestResult = {
    accuracyPitch: summary.accuracyPitch,
    accuracyTiming: summary.accuracyTiming,
    precision: summary.precision,
    coverage: summary.coverage,
    quality: summary.quality,
    tempo: summary.tempo,
    input: inputOf(summary),
    at: at.toISOString(),
  };
  const passedNow = isPassed(summary, targetTempo);
  const handsEntry: HandsProgress = {
    attempts: previousHands.attempts + 1,
    lastAt: at.toISOString(),
    status: previousHands.status === 'passed' || passedNow ? 'passed' : 'started',
    bestWait: summary.mode === 'wait' ? better(previousHands.bestWait, result) : previousHands.bestWait,
    bestTempo: summary.mode === 'tempo' ? better(previousHands.bestTempo, result) : previousHands.bestTempo,
  };
  const scorePassed = previous.status === 'passed' || (passedNow && summary.fullHands);
  const entry: ScoreProgress = {
    attempts: previous.attempts + 1,
    lastAt: at.toISOString(),
    status: scorePassed ? 'passed' : 'started',
    hands: { ...previous.hands, [key]: handsEntry },
  };
  return {
    ...progress,
    scores: { ...progress.scores, [scoreId]: entry },
    totalSessions: progress.totalSessions + 1,
    totalPracticeMs: progress.totalPracticeMs + Math.max(0, durationMs),
    lastScoreId: scoreId,
  };
}

interface LegacyBest {
  accuracyPitch?: number;
  accuracyTiming?: number;
  tempo?: number;
  at?: string;
}

interface LegacyScore {
  attempts?: number;
  lastAt?: string;
  status?: string;
  bestWait?: LegacyBest;
  bestTempo?: LegacyBest;
}

function migrateBest(b: LegacyBest | undefined): BestResult | undefined {
  if (!b || typeof b.accuracyPitch !== 'number') return undefined;
  const accuracyPitch = b.accuracyPitch;
  const accuracyTiming = typeof b.accuracyTiming === 'number' ? b.accuracyTiming : accuracyPitch;
  return { accuracyPitch, accuracyTiming, precision: accuracyPitch, coverage: 1, quality: accuracyPitch, tempo: typeof b.tempo === 'number' ? b.tempo : 0, input: 'unknown', at: b.at ?? '', legacy: true };
}

/** Lit une progression enregistrée, quelle que soit sa version ; null si illisible. */
export function migrateProgress(raw: unknown): Progress | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as { version?: number; scores?: Record<string, unknown>; totalSessions?: number; totalPracticeMs?: number; lastScoreId?: string | null };
  const base = {
    totalSessions: typeof r.totalSessions === 'number' ? r.totalSessions : 0,
    totalPracticeMs: typeof r.totalPracticeMs === 'number' ? r.totalPracticeMs : 0,
    lastScoreId: typeof r.lastScoreId === 'string' ? r.lastScoreId : null,
  };
  if (r.version === 2) {
    return { version: 2, scores: (r.scores as Record<string, ScoreProgress>) ?? {}, ...base };
  }
  if (r.version === 1) {
    const scores: Record<string, ScoreProgress> = {};
    for (const [id, value] of Object.entries(r.scores ?? {})) {
      const s = value as LegacyScore;
      const status: ScoreStatus = s.status === 'new' ? 'new' : 'started';
      const hands: HandsProgress = { attempts: s.attempts ?? 0, lastAt: s.lastAt ?? '', status, bestWait: migrateBest(s.bestWait), bestTempo: migrateBest(s.bestTempo) };
      scores[id] = { attempts: s.attempts ?? 0, lastAt: s.lastAt ?? '', status, hands: { unknown: hands } };
    }
    return { version: 2, scores, ...base };
  }
  return null;
}

export const progressStore = new Store<Progress>('pianohome.progress.v1', DEFAULT_PROGRESS, { migrate: migrateProgress });

export function saveSession(scoreId: string, summary: SessionSummary, targetTempo: number, durationMs: number): void {
  progressStore.set((current) => recordSession(current, scoreId, summary, targetTempo, durationMs));
}
